import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { HomeIcon, UsersIcon, BuildingIcon, LayersIcon, QrCodeIcon, SendIcon, UploadIcon, ShieldAlertIcon, RotateCcwIcon, BarChartIcon, ChevronDownIcon, ChevronRightIcon, LogOutIcon, AlertTriangleIcon, FilesIcon, ListFilterIcon, ActivityIcon, HistoryIcon } from 'lucide-react';
interface SidebarProps {
  isOpen: boolean;
}
interface NavItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  children?: Array<{
    name: string;
    path: string;
  }>;
}
const Sidebar: React.FC<SidebarProps> = ({
  isOpen
}) => {
  const {
    theme
  } = useTheme();
  const {
    logout,
    user
  } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    'User & Role Management': true,
    'Merchant Onboarding': false,
    'Aggregator & Super Merchant': false,
    'Till Management': false,
    'Bulk Operations': false,
    'Dispute Management': false,
    'Transaction Rules Management': false,
    Reports: false
  });
  const toggleExpand = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };
  const navItems: NavItem[] = [{
    name: 'Dashboard',
    icon: <HomeIcon className="h-5 w-5" data-id="element-176" />,
    path: '/dashboard'
  }, {
    name: 'User & Role Management',
    icon: <UsersIcon className="h-5 w-5" data-id="element-177" />,
    children: [{
      name: 'User List',
      path: '/users'
    }, {
      name: 'Role Management',
      path: '/roles'
    }, {
      name: 'Audit Logs',
      path: '/audit-logs'
    }, {
      name: 'Session History',
      path: '/session-history'
    }]
  }, {
    name: 'Merchant Onboarding',
    icon: <BuildingIcon className="h-5 w-5" data-id="element-178" />,
    children: [{
      name: 'Merchant List',
      path: '/merchants'
    }, {
      name: 'New Merchant',
      path: '/merchants/new'
    }]
  }, {
    name: 'Aggregator & Super Merchant',
    icon: <LayersIcon className="h-5 w-5" data-id="element-179" />,
    children: [{
      name: 'Aggregator Management',
      path: '/aggregators'
    }, {
      name: 'Chain Merchant Setup',
      path: '/chain-merchants'
    }, {
      name: 'Hierarchy View',
      path: '/hierarchy'
    }]
  }, {
    name: 'Till Management',
    icon: <LayersIcon className="h-5 w-5" data-id="element-180" />,
    children: [{
      name: 'Till List',
      path: '/tills'
    }, {
      name: 'New Till',
      path: '/tills/new'
    }]
  }, {
    name: 'Bulk Operations',
    icon: <UploadIcon className="h-5 w-5" data-id="element-181" />,
    children: [{
      name: 'Bulk QR Generation',
      path: '/bulk-qr'
    }, {
      name: 'Bulk Merchant Upload',
      path: '/bulk-merchant'
    }]
  }, {
    name: 'Discount Management',
    icon: <FilesIcon className="h-5 w-5" data-id="element-182" />,
    path: '/discount-management'
  }, {
    name: 'Dispute Management',
    icon: <ShieldAlertIcon className="h-5 w-5" data-id="element-183" />,
    children: [{
      name: 'Dispute List',
      path: '/disputes'
    }, {
      name: 'New Dispute',
      path: '/disputes/new'
    }]
  }, {
    name: 'Transaction Rules Management',
    icon: <AlertTriangleIcon className="h-5 w-5" data-id="element-184" />,
    children: [{
      name: 'Dashboard Overview',
      path: '/transaction-rules/dashboard'
    }, {
      name: 'Rules Management',
      path: '/transaction-rules/rules'
    }, {
      name: 'Risk Rule Alerts',
      path: '/transaction-rules/alerts'
    }, {
      name: 'Transaction Monitoring',
      path: '/transaction-rules/monitoring'
    }, {
      name: 'Audit & Logs',
      path: '/transaction-rules/audit-logs'
    }]
  }, {
    name: 'Reports',
    icon: <BarChartIcon className="h-5 w-5" data-id="element-185" />,
    children: [{
      name: 'Transaction Detail',
      path: '/reports/transaction-detail'
    }, {
      name: 'Transaction Summary',
      path: '/reports/transaction-summary'
    }, {
      name: 'Pending Payments',
      path: '/reports/pending-payments'
    }, {
      name: 'Failed Payments',
      path: '/reports/failed-payments'
    }]
  }];
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  return <aside className={`${isOpen ? 'w-64' : 'w-20'} transition-width duration-300 ease-in-out ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} border-r dark:border-gray-700 flex flex-col`} data-id="element-186">
      <div className={`p-4 border-b dark:border-gray-700 flex items-center ${isOpen ? 'justify-start' : 'justify-center'}`} data-id="element-187">
        {isOpen ? <h1 className="text-xl font-bold" data-id="element-188">MMS Admin</h1> : <h1 className="text-xl font-bold" data-id="element-189">MMS</h1>}
      </div>
      {user && isOpen && <div className="p-4 border-b dark:border-gray-700" data-id="element-190">
          <div className="flex items-center" data-id="element-191">
            <div className="h-8 w-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-medium" data-id="element-192">
              {user.name.charAt(0)}
            </div>
            <div className="ml-3" data-id="element-193">
              <p className="text-sm font-medium" data-id="element-194">{user.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-195">
                {user.role}
              </p>
            </div>
          </div>
        </div>}
      <nav className="flex-1 overflow-y-auto py-4" data-id="element-196">
        <ul className="space-y-1 px-2" data-id="element-197">
          {navItems.map(item => <li key={item.name} data-id="element-198">
              {item.children ? <div data-id="element-199">
                  <button onClick={() => toggleExpand(item.name)} className={`flex items-center w-full p-2 rounded-md ${expandedItems[item.name] ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'} transition-colors duration-200`} data-id="element-200">
                    <span className="mr-3 text-gray-500 dark:text-gray-400" data-id="element-201">
                      {item.icon}
                    </span>
                    {isOpen && <>
                        <span className="flex-1 text-left" data-id="element-202">{item.name}</span>
                        {expandedItems[item.name] ? <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-203" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-204" />}
                      </>}
                  </button>
                  {isOpen && expandedItems[item.name] && <ul className="mt-1 pl-6 space-y-1" data-id="element-205">
                      {item.children.map(child => <li key={child.name} data-id="element-206">
                          <Link to={child.path} className={`block p-2 rounded-md ${isActive(child.path) ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'} transition-colors duration-200`} data-id="element-207">
                            {child.name}
                          </Link>
                        </li>)}
                    </ul>}
                </div> : <Link to={item.path || '#'} className={`flex items-center p-2 rounded-md ${isActive(item.path || '') ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'} transition-colors duration-200`} data-id="element-208">
                  <span className="mr-3 text-gray-500 dark:text-gray-400" data-id="element-209">
                    {item.icon}
                  </span>
                  {isOpen && <span data-id="element-210">{item.name}</span>}
                </Link>}
            </li>)}
        </ul>
      </nav>
      <div className="p-4 border-t dark:border-gray-700" data-id="element-211">
        {isOpen ? <button onClick={logout} className="flex items-center w-full p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 text-gray-700 dark:text-gray-300" data-id="element-212">
            <LogOutIcon className="h-5 w-5 mr-3" data-id="element-213" />
            <span data-id="element-214">Logout</span>
          </button> : <button onClick={logout} className="flex justify-center w-full p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 text-gray-700 dark:text-gray-300" data-id="element-215">
            <LogOutIcon className="h-5 w-5" data-id="element-216" />
          </button>}
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center" data-id="element-217">
          {isOpen ? 'MMS v1.0.0' : 'v1.0'}
        </div>
      </div>
    </aside>;
};
export default Sidebar;